/**
 * WF4: Approval Queue
 * Dashboard for supervisors to approve/reject plans
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApprovalQueue = () => {
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [decision, setDecision] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchApprovals();
    fetchStats();
  }, []);

  const fetchApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/approvals/queue?assigned_to_me=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/approvals/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const makeDecision = async () => {
    if (!comment || comment.length < 10) {
      alert('Please provide a comment (minimum 10 characters)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/approvals/${selectedApproval.id}/decision`,
        { decision, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Decision recorded successfully');
      setSelectedApproval(null);
      setComment('');
      fetchApprovals();
      fetchStats();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) return <div className="p-8">Loading approval queue...</div>;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Approval Queue (Maker-Checker)</h1>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.total_pending}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Approved Today</div>
            <div className="text-2xl font-bold text-green-600">{stats.approved_today}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Rejected Today</div>
            <div className="text-2xl font-bold text-red-600">{stats.rejected_today}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Avg Decision Time</div>
            <div className="text-2xl font-bold text-blue-600">{stats.avg_time_to_decision_hours.toFixed(1)}h</div>
          </div>
        </div>
      )}

      {/* Queue Table */}
      <div className="bg-white rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours Pending</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {approvals.map((approval) => (
              <tr key={approval.id}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium">{approval.plan_name || 'N/A'}</div>
                  <div className="text-sm text-gray-500">{approval.plan_company || 'N/A'}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {approval.requester_name || 'Unknown'}
                </td>
                <td className="px-6 py-4 text-center">{approval.priority}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                    {approval.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {approval.hours_pending ? approval.hours_pending.toFixed(1) : '0'} hrs
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => setSelectedApproval(approval)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Review →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {approvals.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No approvals pending. Queue will populate when plans are submitted for approval.
        </div>
      )}

      {/* Decision Modal */}
      {selectedApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Make Decision</h2>
            <div className="mb-4">
              <p><strong>Plan:</strong> {selectedApproval.plan_name}</p>
              <p><strong>Requested by:</strong> {selectedApproval.requester_name}</p>
              <p className="text-sm text-gray-600 mt-2">{selectedApproval.request_comment}</p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Decision</label>
              <select
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select decision...</option>
                <option value="approve">✅ Approve</option>
                <option value="reject">❌ Reject</option>
                <option value="return_for_revision">↩️ Return for Revision</option>
                <option value="escalate">⬆️ Escalate</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Comment (required)</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border rounded px-3 py-2 h-24"
                placeholder="Provide your reasoning..."
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={makeDecision}
                disabled={!decision || !comment}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                Submit Decision
              </button>
              <button
                onClick={() => {
                  setSelectedApproval(null);
                  setDecision('');
                  setComment('');
                }}
                className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>

            {selectedApproval.requested_by && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm font-medium text-yellow-800">⚠️ Maker-Checker Note:</p>
                <p className="text-sm text-yellow-700">You cannot approve your own request. System will validate.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;